import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { authAPI } from '../services/api';

// Debug log to ensure authAPI is properly imported
console.log('AuthContext: authAPI imported:', authAPI);
console.log('AuthContext: authAPI.checkAuth:', typeof authAPI.checkAuth);

interface User {
  _id: string;
  email: string;
  role: 'admin' | 'mentor' | 'aspirant';
  firstName?: string;
  lastName?: string;
  isApproved: boolean;
  isEmailVerified: boolean;
  profileCompleted: number;
  profileImageUrl?: string;
  createdAt: string;
  needsEmailVerification?: boolean;
  needsApproval?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  adminLogin: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasCheckedAuthRef = useRef(false);
  
  console.log('AuthProvider: Component rendered');

  const checkAuthStatus = async () => {
    if (hasCheckedAuthRef.current) {
      console.log('AuthContext: Skipping auth check - already checked');
      return;
    }

    try {
      setIsLoading(true);
      console.log('AuthContext: Checking authentication status...');
      
      const response = await authAPI.getCurrentUser();
      console.log('AuthContext: API response:', response);
      
      if (response.success && response.data && response.data.user) {
        setUser(response.data.user);
        console.log('AuthContext: User authenticated:', response.data.user.email);
      } else {
        setUser(null);
        console.log('AuthContext: No user data in response');
      }
    } catch (error: any) {
      console.log('AuthContext: Authentication check failed:', error.message);
      setUser(null);
    } finally {
      setIsLoading(false);
      hasCheckedAuthRef.current = true;
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []); // Empty dependency array - only run once

  const refreshUser = async () => {
    hasCheckedAuthRef.current = false;
    await checkAuthStatus();
  };

  const login = async (credentials: { email: string; password: string }) => {
    try {
      setIsLoading(true);
      const response = await authAPI.login(credentials);
      if (response.success) {
        await refreshUser();
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const adminLogin = async (credentials: { email: string; password: string }) => {
    try {
      setIsLoading(true);
      const response = await authAPI.adminLogin(credentials);
      if (response.success) {
        await refreshUser();
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsLoading(false);
      hasCheckedAuthRef.current = false;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    adminLogin,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 